/**
 * <fd-fans> — the carousel and the video dialogs of the "Fans at Fandom" section (sections/fd-fans.liquid).
 *
 * Carousel: the cards sit in a horizontally scrolling, snapping row (`[data-fd-track]`). The back/forward buttons
 * (`[data-fd-step="-1" | "1"]`) scroll it by one card, mirrored on right-to-left pages, and are disabled at each
 * end; the buttons are shown only while the cards do not all fit.
 *
 * Dialogs: each card has a `[data-fd-open="<dialog id>"]` button and a `<dialog>` holding a `<video controls>`.
 * Opening plays the video unless the shopper prefers reduced motion; closing (X, Escape or a click on the
 * backdrop) pauses it. Focus returns to the card button because `showModal()` restores it on close.
 */
class FdFans extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    // A new controller per connection, so the listeners are bound again when the element is moved in the DOM.
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#handleClick, { signal });
    // `close` does not bubble, so listen in the capture phase.
    this.addEventListener('close', this.#handleClose, { capture: true, signal });

    const track = this.#track;
    if (track) {
      track.addEventListener('scroll', this.#syncControls, { passive: true, signal });
      this.#resizeObserver = new ResizeObserver(this.#syncControls);
      this.#resizeObserver.observe(track);
      this.#syncControls();
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  get #track() {
    const track = this.querySelector('[data-fd-track]');
    return track instanceof HTMLElement ? track : null;
  }

  /** Shows the buttons only when the row overflows, and disables each one at its end. */
  #syncControls = () => {
    const track = this.#track;
    const controls = this.querySelector('[data-fd-controls]');
    if (!track || !(controls instanceof HTMLElement)) return;

    const max = track.scrollWidth - track.clientWidth;
    // scrollLeft is negative when scrolling a right-to-left row, so compare its size.
    const position = Math.abs(track.scrollLeft);
    controls.hidden = max <= 1;
    for (const button of controls.querySelectorAll('[data-fd-step]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = Number(button.dataset.fdStep) < 0 ? position <= 1 : position >= max - 1;
    }
  };

  /**
   * Scrolls the row by one card (plus the gap) in the reading direction.
   * @param {number} direction - 1 forward, -1 back
   */
  #step(direction) {
    const track = this.#track;
    if (!track) return;
    const card = track.querySelector('.fd-fan-card');
    const style = getComputedStyle(track);
    const distance = (card ? card.getBoundingClientRect().width : track.clientWidth) + (parseFloat(style.columnGap) || 0);
    const sign = style.direction === 'rtl' ? -1 : 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * sign * distance, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const stepper = target.closest('[data-fd-step]');
    if (stepper instanceof HTMLButtonElement && this.contains(stepper)) {
      if (!stepper.disabled) this.#step(Number(stepper.dataset.fdStep) || 1);
      return;
    }

    const opener = target.closest('[data-fd-open]');
    if (opener instanceof HTMLElement && this.contains(opener)) {
      this.#openDialog(opener.dataset.fdOpen || '');
      return;
    }

    if (target.closest('[data-fd-close]')) {
      target.closest('dialog')?.close();
      return;
    }

    // A click on the backdrop is dispatched on the dialog itself, outside its box.
    if (target instanceof HTMLDialogElement && target.open && event instanceof MouseEvent) {
      const box = target.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) target.close();
    }
  };

  /** @param {string} id */
  #openDialog(id) {
    const dialog = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    dialog.showModal();

    const video = dialog.querySelector('video');
    if (video && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.play().catch(() => {
        // The shopper can still press play on the native controls.
      });
    }
  }

  /** @param {Event} event */
  #handleClose = (event) => {
    if (event.target instanceof HTMLDialogElement) {
      event.target.querySelector('video')?.pause();
    }
  };
}

if (!customElements.get('fd-fans')) {
  customElements.define('fd-fans', FdFans);
}
